import { Permission } from 'settings/schema'
import * as yup from 'yup'

interface PermissionDefinition {
    label: string
    prerequisites?: Permission[]
}

export const PERMISSION_DEFINITIONS: Record<Permission, PermissionDefinition> = {
    partner_admin: { label: 'Admin' },
    access_products: { label: 'Access products' },
    view_pricing_settings: { label: 'View pricing settings', prerequisites: ['access_products'] },
    edit_pricing_settings: { label: 'Edit pricing settings', prerequisites: ['view_pricing_settings'] },
    view_product_validity: { label: 'View product validity', prerequisites: ['access_products'] },
    edit_product_validity: { label: 'Edit product validity', prerequisites: ['view_product_validity'] },
    access_orders: { label: 'Access orders', prerequisites: ['access_products'] },
    edit_orders: { label: 'Edit orders', prerequisites: ['access_orders', 'view_pricing_settings'] },
    create_order: { label: 'Create orders', prerequisites: ['edit_orders'] },
    export_orders: { label: 'Export orders', prerequisites: ['access_orders'] },
    refund_orders: { label: 'Refund orders', prerequisites: ['access_orders'] },
    read_carts: { label: 'Access carts' },
    write_carts: { label: 'Edit carts', prerequisites: ['read_carts'] },
    control_panel_login: { label: 'Access Control Panel' },
    access_analytics: { label: 'Access reports', prerequisites: ['control_panel_login'] },
    access_codes: { label: 'Access codes', prerequisites: ['control_panel_login'] },
    view_wonderbar: { label: 'View Engage Tools', prerequisites: ['control_panel_login'] },
    edit_wonderbar: { label: 'Edit Engage Tools', prerequisites: ['view_wonderbar'] },
    pos_login: {
        label: 'Access Point of Sale terminals',
        prerequisites: ['write_carts', 'create_order'],
    },
    access_pulse: { label: 'Access Pulse', prerequisites: ['control_panel_login'] },
    access_pulse_sales: { label: 'View sales data in Pulse', prerequisites: ['access_pulse'] },
    mcp: { label: 'Connect AI tools (MCP)', prerequisites: ['access_pulse'] },
    access_ai_assistant: { label: 'Access AI assistant', prerequisites: ['mcp'] },
}

export const AI_PERMISSIONS: Permission[] = ['access_pulse', 'access_pulse_sales', 'access_ai_assistant', 'mcp']

export const getPrerequisitePermissions = (permission: Permission, checked = new Set<Permission>()): Permission[] => {
    if (checked.has(permission)) {
        return []
    }
    checked.add(permission)

    const prerequisites = PERMISSION_DEFINITIONS[permission].prerequisites || []
    const allPrerequisites: Permission[] = [...prerequisites]
    for (const p of prerequisites) {
        allPrerequisites.push(...getPrerequisitePermissions(p, checked))
    }
    return [...new Set(allPrerequisites)]
}

export const getDependentPermissions = (permission: Permission, checked = new Set<Permission>()): Permission[] => {
    if (checked.has(permission)) {
        return []
    }
    checked.add(permission)

    const dependentPermissions: Permission[] = []
    for (const p of Object.keys(PERMISSION_DEFINITIONS)) {
        if (PERMISSION_DEFINITIONS[p as Permission].prerequisites?.includes(permission)) {
            dependentPermissions.push(p as Permission)
            dependentPermissions.push(...getDependentPermissions(p as Permission, checked))
        }
    }
    return [...new Set(dependentPermissions)]
}

export const userFormValidationSchema = yup.object().shape({
    email: yup.string().email('Please enter a valid email address').required('Please enter an email address'),
    resellerId: yup.string().when('role', {
        is: 'reseller',
        then: yup.string().nullable().required('Please select a reseller'),
        otherwise: yup.string().nullable(),
    }),
    firstName: yup.string().when('permissions.pos_login', {
        is: true,
        then: yup.string().required('Please enter a first name'),
        otherwise: yup.string().nullable(),
    }),
    lastName: yup.string().when('permissions.pos_login', {
        is: true,
        then: yup.string().required('Please enter a last name'),
        otherwise: yup.string().nullable(),
    }),
})
